import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { EditCommonFormComponent } from '../common-form/edit-common-form/edit-common-form.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { ChooseSourceDocComponent } from '../choose-source-doc/choose-source-doc.component';
import { PersonaManagementComponent } from '../persona-management/persona-management.component';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserManagementComponent } from '../user-management/user-management.component';
import { WorkspaceOverviewComponent } from '../workspace-overview/workspace-overview.component';
import { Meta, Title } from '@angular/platform-browser';
import { filter, Subscription } from 'rxjs';
import { StatusManagementComponent } from '../status-management/status-management.component';

@Component({
  selector: 'app-edit-home',
  templateUrl: './edit-home.component.html',
  styleUrls: ['./edit-home.component.scss'],
  imports: [
    EditCommonFormComponent,
    MatExpansionModule,
    MatIconModule,
    ChooseSourceDocComponent,
    PersonaManagementComponent,
    RouterModule,
    UserManagementComponent,
    WorkspaceOverviewComponent,
    StatusManagementComponent
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditHomeComponent implements OnInit, OnDestroy {
  public openPanels: string[] = [];
  readonly panelTitles = {
    'common-info': 'Common information',
    'user-management': 'User management',
    'source-docs': 'Source documents',
    'persona-management': 'Persona management',
    'status-management': 'Status management'
  };
  private readonly sub = new Subscription();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly clipboard: Clipboard,
    private readonly snackbar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
    private readonly meta: Meta,
    private readonly title: Title
  ) {}

  ngOnInit(): void {
    if (this.activatedRoute.snapshot.fragment) {
      this.setInitialOpenPanels(this.activatedRoute.snapshot.fragment);
    }
    window.addEventListener('popstate', this.lateOpen);
    this.sub.add(
      this.activatedRoute.fragment
        .pipe(filter(fragment => fragment !== null && fragment !== undefined))
        .subscribe(fragment => {
          this.setTitle(fragment as keyof typeof this.panelTitles);
        })
    );
    this.meta.updateTag({ name: 'description', content: 'Workspace basic information' });
    this.meta.updateTag({ property: 'og:description', content: 'Workspace basic information' });
  }

  lateOpen = () => {
    setTimeout(() => {
      const fragment = window.location.hash.split('#').join('');
      this.setInitialOpenPanels(fragment);
    });
  };

  setTitle(value: keyof typeof this.panelTitles | undefined) {
    const segmentTitle = value ? this.panelTitles[value] : null;
    const title = `DRIFT - Workspace Home${segmentTitle ? ' - ' + segmentTitle : ''}`;
    this.meta.updateTag({ name: 'title', content: title });
    this.meta.updateTag({ name: 'og:title', content: title });
    this.title.setTitle(title);
    this.cdr.detectChanges();
  }

  private setInitialOpenPanels(initialFragment: string) {
    if (initialFragment) {
      this.openPanels = [initialFragment];
    }
    this.cdr.detectChanges();
  }

  changePanelStatus(fragmentId: string) {
    if (this.openPanels.includes(fragmentId)) {
      this.openPanels = this.openPanels.filter(openPanel => openPanel !== fragmentId);
    } else {
      this.openPanels = [...this.openPanels, fragmentId];
    }
  }

  copyToClipboard(fragmentId: string, event: MouseEvent) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    const currentUrlPath = window.location.pathname + window.location.search;
    const fullUrlWithHash = `${window.location.origin}${currentUrlPath}#${fragmentId}`;

    const success = this.clipboard.copy(fullUrlWithHash);
    if (success) {
      this.snackbar.open('Link copied to clipboard', 'Dismiss', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
    } else {
      this.snackbar.open('Could not copy to clipboard', 'Dismiss', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.lateOpen);
    this.sub.unsubscribe();
  }
}
